import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { PROTOCOLS } from "../lib/protocols";
import { openDiagnostica } from "./DiagnosticaModal";
import { useSectionNavigation } from "../lib/navigation";

const NAV = [
  { id: "obiettivi", label: "OBIETTIVI", hint: "Cosa vuoi ottenere" },
  { id: "metodo", label: "METODO", hint: "Il percorso in 6 fasi" },
  { id: "compensi", label: "COME LAVORIAMO", hint: "Compensi chiari" },
  { id: "protocolli", label: "5 PROTOCOLLI", dropdown: true, hint: "I servizi proprietari" },
  { id: "impresa-partner", label: "IMPRESA PARTNER", hint: "Edilizia Cannino" },
  { id: "piattaforma", label: "PIATTAFORMA", hint: "Il tuo portale" },
  { id: "ecosistema", label: "ECOSISTEMA", hint: "La rete selezionata" },
  { id: "chi-siamo", label: "CHI SIAMO", hint: "Alessio Cannino" },
  { id: "contatti", label: "CONTATTI", hint: "Parla con noi" },
];

export default function NavigationHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const goToSection = useSectionNavigation();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    if (window.history.state && window.history.state.acMenu) {
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeMenu();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeMenu]);

  // il gesto "indietro" del browser chiude il menu invece di abbandonare il sito
  useEffect(() => {
    if (!menuOpen) return;
    window.history.pushState({ acMenu: true }, "");
    const onPop = () => setMenuOpen(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [menuOpen]);

  const scrollRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const updateProgress = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    setScrollProgress(max > 4 ? Math.min(100, Math.round((el.scrollTop / max) * 100)) : 100);
  };

  useEffect(() => {
    if (menuOpen) setTimeout(updateProgress, 60);
  }, [menuOpen]);

  const go = (id) => {
    setMenuOpen(false);
    goToSection(id);
  };

  const NavAnchor = ({ item, mobile = false }) => (
    <a
      href={`/#${item.id}`}
      data-testid={`${mobile ? "mobile" : "nav"}-${item.id}`}
      onClick={(e) => {
        e.preventDefault();
        go(item.id);
      }}
      className={mobile
        ? "block transition-colors duration-200 hover:text-gold-light"
        : "text-[10px] font-semibold tracking-[0.14em] text-[#c6c3bd] transition-colors duration-200 hover:text-gold-light"}
    >
      {mobile ? (
        <>
          <span className="block font-serif text-2xl text-cream">{item.label}</span>
          {item.hint && <span className="mt-1 block text-[10px] uppercase tracking-[0.16em] text-[#777]">{item.hint}</span>}
        </>
      ) : (
        item.label
      )}
    </a>
  );

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b border-[#c9a45c]/20 bg-[#050505]/85 backdrop-blur-xl transition-all duration-300 ${
        scrolled ? "shadow-[0_18px_40px_rgba(0,0,0,0.45)]" : ""
      }`}
      data-testid="site-header"
    >
      <div
        className={`container-hub flex items-center justify-between gap-6 transition-all duration-300 ${
          scrolled ? "h-[66px]" : "h-[84px]"
        }`}
      >
        <Link to="/" data-testid="nav-home-link" onClick={() => setMenuOpen(false)}>
          <Logo compact={scrolled} />
        </Link>

        <nav className="hidden items-center gap-5 xl:flex" aria-label="Navigazione principale">
          {NAV.map((item) =>
            item.dropdown ? (
              <div key={item.id} className="group relative">
                <button
                  data-testid="nav-protocolli"
                  onClick={() => go(item.id)}
                  className="flex items-center gap-1.5 py-4 text-[10px] font-semibold tracking-[0.14em] text-[#c6c3bd] transition-colors duration-200 hover:text-gold-light"
                >
                  {item.label}
                  <ChevronDown size={12} className="transition-transform duration-300 group-hover:rotate-180" />
                </button>
                <div className="invisible absolute right-0 top-full w-[340px] translate-y-2 border border-[#c9a45c]/25 bg-[#0b0b0b]/97 p-2 opacity-0 shadow-[0_30px_60px_rgba(0,0,0,0.6)] backdrop-blur-xl transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  {PROTOCOLS.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/protocollo/${p.slug}`}
                      data-testid={`nav-protocol-${p.slug}`}
                      className="group/item flex items-start gap-4 px-4 py-3 transition-colors duration-200 hover:bg-[#c9a45c]/8"
                    >
                      <span className="mt-0.5 font-mono text-[10px] text-gold">{p.number}</span>
                      <span>
                        <span className="block text-[11px] font-bold tracking-[0.14em] text-cream group-hover/item:text-gold-light">
                          {p.name}
                        </span>
                        <span className="mt-1 block text-[11px] leading-snug text-[#929292]">{p.tagline}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <NavAnchor key={item.id} item={item} />
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-button"
            onClick={() => openDiagnostica()}
            className="btn btn-gold btn-desktop !min-h-[42px] !px-5 !text-[10px]"
          >
            Prenota l'analisi strategica
          </button>
          <button
            data-testid="mobile-menu-toggle"
            aria-label={menuOpen ? "Chiudi il menu" : "Apri il menu"}
            aria-expanded={menuOpen}
            onClick={() => (menuOpen ? closeMenu() : setMenuOpen(true))}
            className="p-2.5 text-cream xl:hidden"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="border-b border-[#c9a45c]/20 bg-[#050505]/98 backdrop-blur-xl xl:hidden"
            aria-label="Navigazione mobile"
            data-testid="mobile-menu"
          >
            <div ref={scrollRef} onScroll={updateProgress} className="mobile-menu-scroll">
              <div className="container-hub flex flex-col gap-4 py-8">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.045 }}
                >
                  <NavAnchor item={item} mobile />
                </motion.div>
              ))}
              <div className="mt-3 border-t border-[#c9a45c]/20 pt-5">
                <p className="mb-3 text-[9px] font-bold tracking-[0.25em] text-gold">I 5 PROTOCOLLI</p>
                <div className="grid grid-cols-1">
                  {PROTOCOLS.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/protocollo/${p.slug}`}
                      data-testid={`mobile-protocol-${p.slug}`}
                      className="flex items-center justify-between gap-3 border-b border-[#c9a45c]/12 py-3 transition-colors hover:text-gold-light"
                    >
                      <span className="flex items-start gap-3">
                        <span className="mt-1 font-mono text-[10px] text-gold">{p.number}</span>
                        <span>
                          <span className="block text-sm text-cream">{p.name}</span>
                          <span className="mt-1 block text-[10px] uppercase tracking-[0.14em] text-[#c9a45c]/90">
                            {p.menuTagline}
                          </span>
                        </span>
                      </span>
                      <span className="mt-1 shrink-0 text-[9px] uppercase tracking-[0.14em] text-[#777]">Scopri</span>
                    </Link>
                  ))}
                </div>
              </div>
              <button
                data-testid="mobile-cta-button"
                onClick={() => {
                  closeMenu();
                  openDiagnostica();
                }}
                className="btn btn-gold mt-4 w-full"
              >
                Prenota l'analisi strategica
              </button>
              </div>
            </div>
            <div className="h-[3px] bg-[#c9a45c]/12" aria-hidden="true">
              <div
                className="h-full bg-gold transition-[width] duration-150 ease-out"
                style={{ width: `${scrollProgress}%` }}
                data-testid="menu-scroll-indicator"
              />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
